import { observer } from 'mobx-react';
import React from 'react';
import { ReactNodeGenerator, isIOS } from '../../../../../../lib/helpers';
import { X9Content, X9ContentSnapshot } from '../../../../../../domain';
import { X9ContentStateStore } from '../../../common/X9ContentStateStore';
import { SyncedAudioModel } from '../../models';
import { AudioElementStore } from '../common/AudioElementStore';
import { AudioProgress } from '../common/AudioProgress';
import { StudentSyncedAudioStore } from './StudentSyncedAudioStore';
import { WithTranslation, withTranslation } from 'react-i18next';

interface Props {
  x9: X9Content | X9ContentSnapshot,
  store: X9ContentStateStore,
  lessonType?: string | undefined
}

function* trackedStudentProgress(store: StudentSyncedAudioStore): ReactNodeGenerator {
  for (let [clientId, progressStore] of store.trackedStudents.entries())
    yield <AudioProgress key={clientId}
      store={progressStore}
      className="student-progress" />;
}

@observer
class StudentPlayButton extends React.Component<{ store: AudioElementStore }> {
  render() {
    const store = this.props.store;
    const classname = store.paused || store.ended
      ? 'fa fa-play'
      : 'fa fa-pause';
    return (
      <button type="button" className="btn btn-outline-primary"
        onClick={store.togglePlay}>
        <span className={classname} />
      </button>);
  }
}

interface IProps extends WithTranslation {
  store: AudioElementStore,
}

@observer
class StudentIOSOverlayComponent extends React.Component<IProps> {
  render() {
    const store = this.props.store;
    return (
      <div onClick={store.toggleVolume} className={!store.paused ? 'student-ios-audio-overlay playing' : 'student-ios-audio-overlay'}>
        <i className="far fa-hand-point-up"></i>
        <p>{this.props.t('classroom.audio.ios-show-unmute')}</p>
      </div>
    );
  }
}

const StudentIOSOverlay = withTranslation()(StudentIOSOverlayComponent);


@observer
class StudentSyncedAudioControls extends React.Component<{ store: StudentSyncedAudioStore, lessonType: string | undefined }> {
  render() {
    const store = this.props.store;
    const lessonType = this.props.lessonType;

    return (
      <div className="row no-gutters">
        <div className="col-auto playerBlockButton">
          {(lessonType !== 'Standard' && lessonType !== 'Demo') && <StudentPlayButton store={store.audio} />}
        </div>
        <div className="col playerBlock">
          <AudioProgress className="student-progress" store={store.progress}
            updateAudio={store.audio} />
          {/* {
            Array.from(trackedStudentProgress(store))
          } */}
        </div>
      </div>
    );
  }
}

@observer
export class StudentSyncedAudio extends React.Component<Props> {
  readonly audioRef = React.createRef<HTMLAudioElement>();

  readonly store: StudentSyncedAudioStore;
  readonly lessonType: string | undefined;

  readonly isIOS = isIOS();

  constructor(props: Readonly<Props>) {
    super(props);
    const model = JSON.parse(props.x9.Contents) as SyncedAudioModel;
    this.store = new StudentSyncedAudioStore(props.x9.Id, model, props.store);
    this.lessonType = props.lessonType;
  }

  render() {
    const store = this.store;

    const handleError = () => {
      store.audio.setAudioError(true);
    }

    return (
      <div className={this.store.audio.muted ? "student-synced-audio muted" : "student-synced-audio"}>
        <audio ref={this.audioRef}
          src={store.model.audioUrl}
          controls={false}
          autoPlay={false}
          loop={false}
          preload="auto"
          muted={this.isIOS}
          onError={handleError} />
        {store.audio.isAudioError && <p className="getUrlSourceError">Audio not found</p>}
        <StudentSyncedAudioControls store={store} lessonType={this.lessonType} />
        {this.isIOS && this.store.audio.muted && <StudentIOSOverlay store={store.audio} />}
      </div>);
  }

  componentDidMount() {
    this.store.audio.setElement(this.audioRef.current!);
  }

  componentWillUnmount() {
    this.store.audio.pause();
    this.store.audio.setCurrentTime(0);
    this.store.audio.setAudioError(false);
    this.store.dispose();
  }
}
